import dayjs, { Dayjs } from 'dayjs';
import { SetURLSearchParams } from 'react-router-dom';
import { type Editor } from '@ckeditor/ckeditor5-core';
import { type FileLoader } from '@ckeditor/ckeditor5-upload';
import { UploadAdapter } from './utils/FileLoaderAdapter';

/**
 * formatDaytJs into a mm/dd/yyyy string
 * @param {DayJs} day in Dayjs type
 * @returns {string} MM/DD/YYYY
 */
export function formatMDY(day: Dayjs) {
  const formattedDate = `${day.month() + 1}/${day.date()}/${day.year()}`;
  return formattedDate;
}

export const calculateNightsBetweenDates = (startDateStr: string, endDateStr: string): number => {
  const startDate = new Date(startDateStr);
  const endDate = new Date(endDateStr);

  // Convert the dates to UTC to avoid timezone issues
  const utcStartDate = Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate());
  const utcEndDate = Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate());

  // Calculate the difference in milliseconds
  const millisecondsPerDay = 24 * 60 * 60 * 1000;
  const differenceInMillis = utcEndDate - utcStartDate;

  // Calculate the number of nights
  const numberOfNights = Math.floor(differenceInMillis / millisecondsPerDay);

  return numberOfNights;
};

export const formatHourFromDateTime = (dateString: string): string => {
  const date = new Date(dateString);

  const hours = date.getHours();
  const minutes = date.getMinutes();

  const formattedHours = String(hours).padStart(2, '0');
  const formattedMinutes = String(minutes).padStart(2, '0');

  return `${formattedHours}:${formattedMinutes} hrs`;
};

//TODO: figurar como hacerlo con i18n despues
export const formatSimpleDate = (dateString: string): string => {
  const months = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  const date = new Date(dateString);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
};

export const formatISOStringToFriendlyDate = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const day = date.getUTCDate();
  const month = monthNames[date.getUTCMonth()];
  const year = date.getUTCFullYear();

  return `${day} ${month} ${year}`;
};

export const formatISOStringToFriendlyDateWithTime = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Convertir de UTC a timezone local del cliente
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  
  // Formatear la hora en timezone local del cliente
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');

  return `${day} ${month} ${year} ${hours}:${minutes}`;
};

export const formatISOStringDate = (isoDateString: string): string => {
  const date = new Date(isoDateString);

  const day = date.getUTCDate();
  const month = date.getUTCMonth();
  const year = date.getUTCFullYear();

  return `${day}/${month + 1}/${year}`;
};
export const getTimeSinceDate = (value: string) => {
  // value is a date as isoString
  const currentDate = dayjs();
  const differenceInMinutos = currentDate.diff(value, 'minute');
  const differenceInHours = currentDate.diff(value, 'hour');
  const differenceInYears = currentDate.diff(value, 'year');
  const differenceInMonths = currentDate.diff(value, 'month');
  const differenceInDays = currentDate.diff(value, 'day');

  let createadSince;
  if (differenceInYears > 1) {
    createadSince = `${differenceInYears} años`;
  } else if (differenceInMonths > 1) {
    createadSince = `${differenceInMonths} meses`;
  } else if (differenceInDays > 1) {
    createadSince = `${differenceInDays} días`;
  } else if (differenceInHours > 1) {
    createadSince = `${differenceInHours} hrs`;
  } else {
    createadSince = `${differenceInMinutos} mins`;
  }
  return createadSince;
};

function hexToRgb(hex: string) {
  let r = 0,
    g = 0,
    b = 0;

  // 3 digits
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  }
  // 6 digits
  else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return [r, g, b];
}

function luminance(r: number, g: number, b: number) {
  const a = [r, g, b].map((v) => {
    const l = v / 255;
    return l <= 0.03928 ? l / 12.92 : Math.pow((l + 0.055) / 1.055, 2.4);
  });
  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function getTextColorFor(hex: string) {
  const [r, g, b] = hexToRgb(hex);
  return luminance(r, g, b) > 0.19 ? 'black' : 'white';
}

/**
 * Updates the current search parameters with the new ones provided without removing the existing ones.
 * @param currentSearchParams - The current URLSearchParams.
 * @param setSearchParams - The function to set new URLSearchParams.
 * @param newParams - The new parameters to add or update.
 */
export const updateSearchParams = (
  currentSearchParams: URLSearchParams,
  setSearchParams: SetURLSearchParams,
  newParams: Record<string, string>,
) => {
  const updatedSearchParams = new URLSearchParams(currentSearchParams);

  Object.keys(newParams).forEach((key) => {
    if (newParams[key]) {
      updatedSearchParams.set(key, newParams[key]);
    } else {
      updatedSearchParams.delete(key);
    }
  });

  setSearchParams(updatedSearchParams);
};

export const getArrayParam = (val: string | null) => (val !== '' && val ? val.split('|') : undefined);

export function createUploadAdapterPlugin(showToast: (message: string) => void) {
  return function UploadAdapterPlugin(editor: Editor) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: FileLoader) =>
      new UploadAdapter(loader, showToast);
  };
}

type InputType = Record<string, string[]>;

type OutputType<T extends InputType> = {
  [K in keyof T]: string;
};

export const arrayToPipeDelimited = <T extends InputType>(obj: T): OutputType<T> => {
  const result = {} as OutputType<T>;
  for (const key in obj) {
    if (Array.isArray(obj[key])) {
      result[key] = obj[key].join('|');
    } else {
      result[key] = obj[key] as unknown as string;
    }
  }
  return result;
};

/**
 * Calculates the exact age based on the given date of birth and an optional relative date.
 *
 * @param dateOfBirth - The date of birth in `YYYY-MM-DD` format.
 * @param toRelativeDate - (Optional) The reference date in `YYYY-MM-DD` format to calculate the age against.
 *                          If not provided, the current date will be used.
 * @returns The calculated age in years.
 */
export const getExactAgeForDOB = (dateOfBirth: string, toRelativeDate?: string): number => {
  const today = toRelativeDate ? new Date(toRelativeDate) : new Date();
  const birthDate = new Date(dateOfBirth);

  const age = today.getFullYear() - birthDate.getFullYear();
  const hasHadBirthdayThisYear =
    today.getMonth() > birthDate.getMonth() ||
    (today.getMonth() === birthDate.getMonth() && today.getDate() >= birthDate.getDate());

  return hasHadBirthdayThisYear ? age : age - 1;
};

/**
 * Converts a given date in DD/MM/YYYY in a Date() object.
 * @param input -  DD/MM/YYYY format string.
 */
export const fromDDMMYYYYToDate = (input: string) => {
  const [day, month, year] = input.split('/').map(Number);

  if (!day || !month || !year) return null;

  const date = new Date(year, month - 1, day);
  const isValid = date.getDate() === day && date.getMonth() === month - 1 && date.getFullYear() === year;

  return isValid ? date : null;
};

/**
 * Converts a given date in DD/MM/YYYY format to YYYY-MM-DD format.
 * @param input -  DD/MM/YYYY format string.
 */
export const fromDDMMYYYYToYYYYMMDD = (input: string): string => {
  const [day, month, year] = input.split('/');
  if (!day || !month || !year) return '';
  return `${year}-${month}-${day}`;
};

/**
 * Converts a given date in YYYY-MM-DD format to DD/MM/YYYY format.
 * @param input -  YYYY-MM-DD format string.
 */
export const fromYYYYMMDDToDDMMYYYY = (input: string): string => {
  const [year, month, day] = input.split('-');
  if (!day || !month || !year) return '';
  return `${day}/${month}/${year}`;
};

/**
 * Converts a given date in date format to DD/MM/YYYY format.
 * @param input - date format string.
 */
export const fromDateToDDMMYYYY = (input: string | Date): string => {
  const date = new Date(input);

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Converts a given date in date format to DD-MM-YYYY format.
 * @param input - date format string.
 */
export const fromDateToYYYYMMDD = (input: string | Date): string => {
  const date = new Date(input);

  if (isNaN(date.getTime())) return '';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${year}-${month}-${day}`;
};

/**
 * Checks if a date string in DD-MM-YYYY format is valid.
 *
 * @param dateStr - The date string in 'DD-MM-YYYY' format.
 * @returns True if the date is valid, false otherwise.
 */
export const isValidDDMMYYYY = (dateStr: string): boolean => {
  const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateStr.match(regex);

  if (!match) return false;

  const [_, dayStr, monthStr, yearStr] = match;
  const day = parseInt(dayStr, 10);
  const month = parseInt(monthStr, 10) - 1; // JS months are 0-indexed
  const year = parseInt(yearStr, 10);

  const date = new Date(year, month, day);

  return (
    date.getFullYear() === year &&
    date.getMonth() === month &&
    date.getDate() === day
  );
};

/**
 * Autocorrect a given date in DD/MM/YYYY format.
 * @param input -  DD/MM/YYYY format string.
 * @param maxDate - Optional. Number. If not passed new `Date().getFullYear()` will be used
 * @param minDate - Optional. Number. If not passed new 1900 will be used
 */
export const autoCorrectDate = (params: { input: string; maxDate?: number; minDate?: number }): string => {
  let { maxDate, minDate } = params;
  const { input } = params;
  maxDate = maxDate || new Date().getFullYear();
  minDate = minDate || 1900;
  const previousValue = input;
  const numbers = input.replace(/\D/g, '');
  let day = numbers.slice(0, 2);
  let month = numbers.slice(2, 4);
  let year = numbers.slice(4, 8);

  const previousParts = previousValue.split('/');
  const prevDay = previousParts[0] || '';
  const prevMonth = previousParts[1] || '';
  const prevYear = previousParts[2] || '';

  if (day.length === 2) {
    const d = parseInt(day);
    if (isNaN(d) || d < 1) {
      day = '01';
    } else if (d > 31) {
      day = '31';
    } else {
      day = d < 10 ? `0${d}` : `${d}`;
    }
  } else {
    day = prevDay;
  }

  if (month.length === 2) {
    const m = parseInt(month);
    if (isNaN(m) || m < 1) {
      month = '01';
    } else if (m > 12) {
      month = '12';
    } else {
      month = m < 10 ? `0${m}` : `${m}`;
    }
  } else {
    month = prevMonth;
  }

  if (year.length === 4) {
    const y = parseInt(year);
    if (isNaN(y) || y < minDate) {
      year = `${minDate}`;
    } else if (y > maxDate) {
      year = `${maxDate}`;
    } else {
      year = `${y}`;
    }
  } else {
    year = prevYear;
  }

  let formatted = day;
  if (month) formatted += `/${month}`;
  if (year) formatted += `/${year}`;

  return formatted;
};

export const toProperName = (input: string): string => {
  const wordsToAvoid: string[] = ['NH'];
  const lowerCaseWords = new Set([
    // Español
    'de',
    'del',
    'la',
    'las',
    'el',
    'los',
    'y',
    'o',
    'u',
    'en',
    'por',
    'con',
    'a',
    // Inglés
    'of',
    'the',
    'and',
    'or',
    'in',
    'on',
    'at',
    'to',
    'for',
    'by',
    'with',
    // Portugués
    'da',
    'do',
    'das',
    'dos',
    'e',
    'em',
    'por',
    'com',
    'a',
    'o',
    'as',
    'os',
  ]);

  const preserveSet = new Set(wordsToAvoid.map((w) => w.toLowerCase()));

  const capitalize = (word: string): string => {
    const isPreserved = preserveSet.has(word.toLowerCase());
    if (isPreserved) {
      return wordsToAvoid.find((w) => w.toLowerCase() === word.toLowerCase())!;
    }

    return word
      .split(/([-'])/)
      .map((part) => (part.match(/^[-']$/) ? part : part.charAt(0).toUpperCase() + part.slice(1)))
      .join('');
  };

  return input
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      if (index === 0 || !lowerCaseWords.has(word)) {
        return capitalize(word);
      }
      return word;
    })
    .join(' ');
};

export const calculateNights = (checkIn: Date | string, checkOut: Date | string): number => {
  const inDate = new Date(checkIn);
  const outDate = new Date(checkOut);

  inDate.setHours(0, 0, 0, 0);
  outDate.setHours(0, 0, 0, 0);

  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const diffMs = outDate.getTime() - inDate.getTime();
  return Math.max(Math.floor(diffMs / millisecondsPerDay), 0);
};

export const formatMiles = (n: number): string => new Intl.NumberFormat('es-UY').format(n);

export const isValidEmail = (email: string) => {
  if (!email) return false;

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};
